import { useState } from 'react'
import './NewAgentDialog.css'

interface NewAgentDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: {
    name: string
    tool_command: string
    prompt: string
  }) => Promise<void>
}

function NewAgentDialog({ open, onClose, onSubmit }: NewAgentDialogProps) {
  const [name, setName] = useState('')
  const [toolCommand, setToolCommand] = useState('')
  const [prompt, setPrompt] = useState('')
  const [submitting, setSubmitting] = useState(false)

  if (!open) return null

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    try {
      await onSubmit({
        name: name || 'New Agent',
        tool_command: toolCommand,
        prompt,
      })
      resetForm()
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  function resetForm() {
    setName('')
    setToolCommand('')
    setPrompt('')
  }

  function handleCancel() {
    resetForm()
    onClose()
  }

  return (
    <div data-testid="new-agent-dialog-overlay" className="dialog-overlay" onClick={handleCancel}>
      <div
        data-testid="new-agent-dialog"
        className="dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dialog-header">
          <h2 className="dialog-title">Create New Agent (Immutable)</h2>
          <button
            type="button"
            data-testid="close-agent-dialog-btn"
            className="dialog-close-btn"
            onClick={handleCancel}
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <p className="dialog-subtitle">
          Agents are immutable after creation. Please review settings carefully.
        </p>

        <form onSubmit={handleSubmit} className="dialog-form">
          <div className="dialog-field">
            <label className="dialog-label" htmlFor="agent-name">
              Agent Name:
            </label>
            <input
              id="agent-name"
              data-testid="agent-name-input"
              type="text"
              className="dialog-input"
              placeholder="e.g., My Claude Agent"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="dialog-field">
            <label className="dialog-label" htmlFor="agent-tool-command">
              Tool Command:
            </label>
            <input
              id="agent-tool-command"
              data-testid="agent-tool-command-input"
              type="text"
              className="dialog-input"
              placeholder="e.g., claude"
              value={toolCommand}
              onChange={(e) => setToolCommand(e.target.value)}
              required
            />
          </div>

          <div className="dialog-field">
            <label className="dialog-label" htmlFor="agent-prompt">
              Optional Custom Prompt:
            </label>
            <textarea
              id="agent-prompt"
              data-testid="agent-prompt-input"
              className="dialog-textarea"
              placeholder="Enter special instructions..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={4}
            />
          </div>

          <div className="dialog-actions">
            <button
              type="button"
              data-testid="cancel-agent-btn"
              className="dialog-btn-cancel"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="create-agent-btn"
              className="dialog-btn-submit"
              disabled={submitting}
            >
              {submitting ? 'Creating...' : 'Create Agent'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default NewAgentDialog
